/**
 * @file 直播测试
 */
import ReactDOM from 'react-dom';
import Player from 'xgplayer';
import HlsPlugin from 'xgplayer-hls';
import FlvPlugin from 'xgplayer-flv';
import { MutableRefObject, useEffect, useRef, useState } from 'react';
import definition from 'xgplayer/es/plugins/definition';
import {
  generateParams,
  getStreamList,
  isMobile,
  isWxBrowser,
  sendPegasusLog,
  setElementStyle
} from '@/utils';
import time from 'xgplayer/es/plugins/time';
import LineControl from './LineControl';
import useInterval from '@/hooks/useInterval';

import s from './index.module.css';
import { lineEnum } from '@/core/constant';

export const heartbeatInterval = 300000; // 5min

const Live = () => {
  const { id } = generateParams() || {};
  const { mySetInterval, clearMyInterval } = useInterval();
  const [lineChecked, setLineChecked] = useState(lineEnum.line1); // 选中的线路
  const [errText, setErrText] = useState('');
  const [videoStatus, setVideoStatus] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFirPlay, setIsFirPlay] = useState(true); // 判断是否已经播放过
  const videoRef = useRef<any>();
  const videoStateInterval = useRef<any>(); // 视频流状态轮询
  const readyStateInterval = useRef<any>(); // 视频流状态轮询
  const timer = useRef() as MutableRefObject<any>; // 心跳计时器
  const retryTimer = useRef() as MutableRefObject<any>; // 重试计时器
  const retryTimes = useRef<number>(0); // 重试计数
  const getTimes = useRef<number>(0); // 拉流计数
  const seq = useRef<number>(0); // 记录心跳发送的次数

  const initPlayer = () => {
    const list = getStreamList(lineChecked);
    // 若存在则销毁
    if (videoRef.current) {
      videoRef.current.destroy();
    }
    videoRef.current = new Player({
      id: 'vs',
      isLive: true,
      width: '100%',
      height: '100%',
      playsinline: true,
      webkitPlaysinline: true,
      'x5-video-player-type': 'h5',
      'x5-video-player-fullscreen': false,
      'x5-video-orientation': 'portraint',
      keyboard: {
        keyCodeMap: {
          //禁用空格键切换播放/暂停
          space: {
            disable: true
          }
        }
      },
      plugins: [...(isMobile ? [HlsPlugin] : [FlvPlugin]), definition, time],
      definitionActive: 'hover',
      ignores: ['cssfullscreen', 'playbackrate'],
      marginControls: true,
      loop: false,
      poster: 'https://ydschool-video.nosdn.127.net/1689239305279poster.png',
      mobile: {
        disableActive: true
      },
      definition: {
        list
      }
    });
  };

  // 全局保持轮询播放器状态
  const pollingGetStatus = () => {
    if (videoStateInterval.current) {
      clearInterval(videoStateInterval.current);
    }
    videoStateInterval.current = setInterval(() => {
      const noStream = videoRef.current.readyState === 0;
      const hasStream = videoRef.current.readyState === 4;
      console.log('pollingGetStatus', videoRef.current.readyState);
      // 持续拉流超过30次直接异常提示 看看能不能捕捉到黑屏
      if (noStream) {
        // isFirPlay不是未播放过的状态下
        if (!isFirPlay) {
          setIsLoading(true);
        }
        if (!errText && !isFirPlay && getTimes.current === 30) {
          console.log('noStream > 30 times, jump errorPage');
          setErrText('err');
          clearInterval(videoStateInterval.current);
          getTimes.current = 0;
          return;
        }
        getTimes.current++;
      }
      if (hasStream) {
        setErrText('');
        getTimes.current = 0;
      }
      setVideoStatus(videoRef.current.readyState);
    }, 2000);
  };

  // 轮询获取流状态
  // checkTimes 表示是首次进入直播
  const pollingGetStreamStatus = (checkTimes?: boolean) => {
    if (readyStateInterval.current) {
      clearInterval(readyStateInterval.current);
    }
    readyStateInterval.current = setInterval(() => {
      // 不是微信环境下 & 首次拉流超过100次直接异常提示
      if (!isWxBrowser && checkTimes && getTimes.current === 100) {
        setErrText('err');
        clearInterval(readyStateInterval.current);
        getTimes.current = 0;
        return;
      }
      getTimes.current++;
      // 异常状态获取
      if (videoRef.current.readyState !== 0) {
        console.log('Video is ready to play');
        setIsFirPlay(false);
        setIsLoading(false);
        clearInterval(readyStateInterval.current);
        if (retryTimer.current) clearMyInterval(retryTimer.current);
        changePlayBtnStatus();
        retryTimes.current = 0;
        getTimes.current = 0;
        if (checkTimes) {
          const playerBtn = document.getElementsByClassName('xgplayer-start')[0] as HTMLElement;
          playerBtn?.classList?.remove('hide');
        }
      }
    }, 500);
  };

  useEffect(() => {
    pollingGetStatus();
    pollingGetStreamStatus(true);

    return () => {
      clearInterval(readyStateInterval.current);
    };
  }, []);

  useEffect(() => {
    console.log('videoStatus', videoStatus);
    if ([1, 4, 3].includes(videoStatus)) {
      setIsLoading(false);
    }
  }, [videoStatus]);

  useEffect(() => {
    if (!videoRef.current) return;
    buffLoading();
    const list = getStreamList(lineChecked);
    videoRef.current.config.definition.list = list;
    videoRef.current.switchURL(list[0].url);
  }, [lineChecked]);

  // 发送心跳
  const sendHeart = (action?: string, options?: Record<string, any>) => {
    try {
      const curAction = action || 'heartbeat';
      sendPegasusLog(curAction, {
        lectureId: id,
        seq: seq.current,
        version: isMobile ? 'app' : 'web',
        ...(options ? { ...options } : {})
      });
      console.log('action', action);
    } catch {
      //
    }
  };

  // 开始心跳
  const startTimer = () => {
    clearMyInterval(timer);
    mySetInterval(timer, heartbeatInterval, sendHeart);
    seq.current = seq.current++;
  };

  // 结束心跳
  const stopHeart = () => {
    // 暂停立即发送一次心跳
    clearMyInterval(timer);
  };

  useEffect(() => {
    initPlayer();
    sendHeart('firstInLive');
  }, []);

  // xgplayer-loading展示
  const showLoading = () => {
    const a = document.getElementsByClassName('xgplayer-loading')[0] as HTMLElement;
    setElementStyle(a, 'display', 'block');
  };

  // xgplayer-loading展示
  const hideLoading = () => {
    const a = document.getElementsByClassName('xgplayer-loading')[0] as HTMLElement;
    setElementStyle(a, 'display', 'none');
  };

  useEffect(() => {
    if (isLoading) {
      showLoading();
    } else {
      hideLoading();
    }
  }, [isLoading]);

  /**
   * 重试播放
   */
  const reloadPlayer = () => {
    const list = getStreamList(lineChecked);
    videoRef.current.switchURL(list[0].url);
    videoRef.current.load();
  };

  /**
   * 自动重试逻辑
   */
  const retryStream = () => {
    try {
      if (errText) return;
      console.log('retryStream');
      if (retryTimes.current < 4) {
        retryTimer.current = setTimeout(() => {
          reloadPlayer();
          retryTimes.current++;
          clearTimeout(retryTimer.current);
        }, 3000);
      }
    } catch {
      //
    }
  };

  /**
   * 控制播放操作
   * @param type disabled | ''
   * @returns
   */
  const changePlayBtnStatus = (type?: string) => {
    const videoPlayer = videoRef.current;
    if (isWxBrowser || !videoPlayer) return;
    const playButton = videoPlayer?.root.querySelector('.xgplayer-play');
    const startButton = videoPlayer?.root.querySelector('.xgplayer-start');
    // 禁用播放按钮
    if (type === 'disabled') {
      setElementStyle(playButton, 'pointerEvents', 'none');
      setElementStyle(playButton, 'opacity', '0.5');
      setElementStyle(startButton, 'pointerEvents', 'none');
      setElementStyle(startButton, 'opacity', '0.5');
      playButton?.setAttribute('disabled', true);
      startButton?.setAttribute('disabled', true);
      // 通过插入一个覆盖整个播放器的透明div来禁用点击事件
      const coverDiv = document.createElement('div');
      coverDiv.className = 'cover-div';
      coverDiv.style.width = '100%';
      coverDiv.style.height = '100%';
      coverDiv.style.position = 'absolute';
      coverDiv.style.top = '0';
      coverDiv.style.zIndex = '9';
      videoPlayer?.root?.appendChild(coverDiv);
      return;
    }
    // 启动播放按钮
    if (playButton && startButton) {
      playButton.style.pointerEvents = '';
      playButton.style.opacity = 1;
      startButton.style.pointerEvents = '';
      startButton.style.opacity = 1;
    }
    // 移除原来覆盖的div
    const coverDiv = document.getElementsByClassName('cover-div')[0] as HTMLElement;
    setElementStyle(coverDiv, 'display', 'none');
  };

  /**
   * 创建隐藏容器并将 LineControl 组件渲染到其中
   * @returns
   */
  const getLineNode = () => {
    const hiddenContainer = document.createElement('div');
    ReactDOM.render(
      <LineControl
        sendHeart={sendHeart}
        lineChecked={lineChecked}
        setLineChecked={setLineChecked}
      />,
      hiddenContainer
    );
    return hiddenContainer.firstChild;
  };

  useEffect(() => {
    const videoPlayer = videoRef.current;
    if (!videoPlayer) return;
    addPlayerListener();

    // 以下是针对首次可以隐藏播放按钮的环境
    videoPlayer.once('ready', () => {
      // 隐藏异常页面
      const errorXplayer = document.getElementsByClassName('xgplayer-error')[0] as HTMLElement;
      setElementStyle(errorXplayer, 'display', 'none');
      // 创建一个Live文本
      if (isMobile) {
        const playDOM = document.getElementsByClassName('xgplayer-play')[0];
        const liveText = document.createElement('div');
        liveText.className = 'xgplayer-live-text';
        liveText.style.display = 'block';
        liveText.innerText = 'Live';

        // 将这个 div 元素添加到控制栏中
        playDOM?.parentNode?.insertBefore(liveText, playDOM);
      }
      // 将线路控件添加到control上
      const definitionDOM = document.getElementsByClassName('xgplayer-definition')[0];
      const lineNode = getLineNode();
      if (lineNode) {
        definitionDOM?.parentNode?.insertBefore(lineNode, definitionDOM);
      }

      // 微信环境必须用播放来触发首次加载视频流
      if (isWxBrowser) {
        setIsLoading(false);
        return;
      }
      // 默认禁用播放按钮 资源加载成功后可以点击
      changePlayBtnStatus('disabled');
      // 初始化隐藏播放按钮
      const playerBtn = document.getElementsByClassName('xgplayer-start')[0] as HTMLElement;
      playerBtn.className = `${playerBtn.className} hide`;
    });

    return () => {
      removePlayerListerner();
    };
  }, [videoRef.current]);

  const onError = (e: any) => {
    sendHeart('error', { errMsg: JSON.stringify(e.message) });
    console.log('error', JSON.stringify(e.message));
    // 微信环境下首次进入不展示err Loading
    if (!(isWxBrowser && isFirPlay)) {
      setIsLoading(true);
    }
    if (retryTimes.current === 3) {
      setErrText(JSON.stringify(e.message));
      setIsLoading(false);
      return;
    }
    retryStream();
  };

  const onPlay = () => {
    sendHeart('play');
    startTimer(); // 开启心跳
  };

  const onPause = () => {
    sendHeart('pause');
    stopHeart();
  };

  const onEnded = () => {
    sendHeart('end');
    // setIsLoading(true);
    // setErrText('');
    // if (retryTimes.current === 2) {
    //   setIsLoading(false);
    //   return;
    // }
    // retryStream();
  };

  const onFullScreenChange = () => {
    sendHeart('fullScreen');
  };

  // 缓冲
  const buffLoading = () => {
    setIsLoading(true);
    setVideoStatus(0);
  };

  const onDefinitionChange = (val: any) => {
    buffLoading();
    sendHeart('definitionChange', {
      originDefinition: val?.from?.definition,
      toDefinition: val?.to?.definition
    });
  };

  const onCanPlay = () => {};

  // video监听的事件
  const addPlayerListener = () => {
    const MyPlayer = videoRef?.current;
    if (!MyPlayer || !MyPlayer.on) return;
    MyPlayer.on('error', onError); // 当在音频/视频加载期间发生错误时触发。
    MyPlayer.on('play', onPlay); // 当音频/视频在因缓冲而暂停或停止后已就绪时触发。
    MyPlayer.on('canplay', onCanPlay); // 当音频/视频在因缓冲而暂停或停止后已就绪时触发。
    MyPlayer.on('pause', onPause); // 当音频/视频已暂停时触发。
    MyPlayer.on('ended', onEnded); // // 当音频/视频播放结束时触发。
    MyPlayer.on('fullscreenchange', onFullScreenChange); // 切换全屏
    MyPlayer.on('definition_change', onDefinitionChange); // 切换清晰度
  };

  const removePlayerListerner = () => {
    const MyPlayer = videoRef?.current;
    if (!MyPlayer || !MyPlayer.off) return;
    MyPlayer.off('error', onError);
    MyPlayer.off('play', onPlay);
    MyPlayer.off('pause', onPause);
    MyPlayer.off('ended', onEnded);
    MyPlayer.off('fullscreenchange', onFullScreenChange);
  };

  return (
    <div className={s.container}>
      {errText && (
        <div className={s.errorCard}>
          <div className={s.errorCardContent}>
            <img
              alt=""
              src="https://ydschool-video.nosdn.127.net/1689252639455%E7%BB%84+3+%281%29.png"
              className={s.card}
            />
            <div className={s.reload}>
              <span>The live broadcast hasn't started. It will be back shortly. </span>
              <span onClick={() => window.location.reload()} className={s.reloadBtn}>
                refresh
              </span>
            </div>
          </div>
        </div>
      )}
      <div id="vs" />
    </div>
  );
};

export default Live;
